import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useConversation } from '../context/ConversationContext';
import rehypeRaw from 'rehype-raw';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  attachment_ids?: string[];
  image_url?: string;
  document_name?: string;
  document_names?: string[];
  sources?: { [key: string]: { title: string; url: string; snippet?: string } };
}

interface MessageListProps {
  messages: Message[];
}

const formatMessage = (message: string) => {
  // Safety check for undefined or null message
  if (!message) {
    return '';
  }
  
  // Convert URL Citation lines to markdown links
  let formatted = message.replace(/\n/g, "  \n").replace(/<br>/g, '\n\n');

  // Then remove leading hyphens from the beginning of lines
  formatted = formatted.replace(/^- /gm, '');

  formatted = formatted.replace(/【.*?†source】/g, '');

  formatted = formatted.replace(/URL Citation:.*(\n)?/g, '');

  return formatted;
};

const MessageList: React.FC<MessageListProps> = ({ messages }) => {
  const endOfMessagesRef = useRef<HTMLDivElement>(null);
  const { loadingMessage, streamingText, isStreaming } = useConversation(); // sendMessage not used directly anymore
  const [processedMessages, setProcessedMessages] = useState<Message[]>([]);

  // Process messages to ensure document_names persistence
  useEffect(() => {
    const enrichedMessages = messages.map(message => {
      return { 
        ...message,
        role: message.role as 'user' | 'assistant' // Ensure correct typing for all messages
      };
    });

    setProcessedMessages(enrichedMessages);
  }, [messages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [processedMessages, loadingMessage, streamingText, isStreaming]);

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Copy message content
  const copyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Add: Copy rendered HTML preserving formatting (bold/italics) and strip horizontal rules
  const copyMessageAsHtml = async (index: number) => {
    try {
      const element = document.getElementById(`msg-content-${index}`);
      if (!element) return;
      let html = element.innerHTML;
      // Remove horizontal rules coming from markdown '---'
      html = html.replace(/<hr\s*\/?>/gi, '');

      // Create plain text fallback
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const plain = (tmp.textContent || '').trim();

      const ClipboardItemCtor: any = (window as any).ClipboardItem;
      if (ClipboardItemCtor && (navigator.clipboard as any).write) {
        const item = new ClipboardItemCtor({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        });
        await (navigator.clipboard as any).write([item]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
    } catch (err) {
      try {
        // Fallback to plain text
        const element = document.getElementById(`msg-content-${index}`);
        if (!element) return;
        await navigator.clipboard.writeText(element.textContent || '');
      } catch (e) {
        console.error('Failed to copy message:', e);
      }
    }
  };

  // Retry message - trigger ChatInput to re-send the user message
  const retryMessage = async (messageIndex: number) => {
    // Find the user message that this assistant response is replying to
    const userMessageIndex = messageIndex - 1;
    if (userMessageIndex >= 0 && processedMessages[userMessageIndex].role === 'user') {
      const userMessage = processedMessages[userMessageIndex].content;
      
      
      // Use the global submit function exposed by ChatInput
      if ((window as any).submitMessageDirectly) {
        (window as any).submitMessageDirectly(userMessage);
      }
    }
  };

  // Render code blocks with syntax highlighting
  const components = {
    code({ node, inline, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <div className="bg-gray-900 rounded-md overflow-hidden border border-gray-700 my-2 max-w-[240px] xs:max-w-xs sm:max-w-sm md:max-w-lg lg:max-w-2xl xl:max-w-4xl">
          <div className="flex items-center justify-between px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-800 border-b border-gray-700">
            <div className="flex items-center gap-1">
              <div className="flex gap-0.5 sm:gap-1">
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-red-500 rounded-full"></div>
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-yellow-500 rounded-full"></div>
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-green-500 rounded-full"></div>
              </div>
              <span className="text-gray-300 text-xs ml-0.5 sm:ml-1 truncate">{match[1]}</span>
            </div>
            <button 
              onClick={() => copyToClipboard(String(children))}
              className="inline-flex items-center justify-center gap-1 whitespace-nowrap text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0 rounded text-gray-400 hover:text-white hover:bg-gray-700 h-4 w-4 sm:h-5 sm:w-5 p-0.5 sm:p-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy w-2.5 h-2.5 sm:w-3 sm:h-3" aria-hidden="true">
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
              </svg>
            </button>
          </div>
          <div className="p-1.5 sm:p-2 overflow-x-auto">
            <div className="min-w-max">
        <SyntaxHighlighter
          style={atomDark}
          language={match[1]}
          PreTag="div"
                customStyle={{
                  fontSize: '9px',
                  lineHeight: '1.1',
                  margin: 0,
                  padding: 0,
                  minWidth: 'fit-content'
                }}
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
            </div>
          </div>
        </div>
      ) : (
        <code className={`${className} text-xs bg-gray-100 px-1 py-0.5 rounded`} {...props}>
          {children}
        </code>
      );
    }
  };

  return (
    <div className="space-y-3">
      {processedMessages.map((message, index) => (
        <div
          key={index}
          className={`flex gap-2 ${message.role === 'user' ? 'justify-end' : ''}`}
        >
          {message.role === 'assistant' && (
            <span className="relative flex shrink-0 overflow-hidden rounded-full w-6 h-6 mt-1 flex-shrink-0">
              <span className="flex h-full w-full items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold text-xs">R</span>
            </span>
          )}
          
          <div className={`flex-1 max-w-[280px] xs:max-w-sm sm:max-w-md md:max-w-3xl lg:max-w-3xl xl:max-w-4xl 2xl:max-w-7xl ${message.role === 'user' ? 'flex justify-end' : ''}`}>
            <div className={`rounded-lg px-1.5 sm:px-2 md:px-3 py-1 sm:py-1.5 md:py-2 ${
              message.role === 'user' 
                ? 'bg-blue-500 text-white rounded-br-md shadow-sm' 
                : 'bg-gray-50 rounded-tl-md border border-gray-200'
            }`}>
              <div className="prose prose-gray max-w-none prose-xs sm:prose-sm">
                {message.image_url && (
                  <img
                    src={message.image_url}
                    alt="Generated" 
                    className="max-w-full h-auto rounded-lg mb-2"
                  />
                )}
                <div className="text-sm leading-relaxed mb-0">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                    components={components}
                    >
                      {formatMessage(message.content)}
                    </ReactMarkdown>
                  </div>
              </div>
              
              {/* Action buttons for assistant messages */}
              {message.role === 'assistant' && (
                <div className="flex items-center gap-1 mt-1.5 sm:mt-2">
                  <button
                    onClick={() => copyMessageAsHtml(index)}
                    className="inline-flex items-center justify-center gap-1 whitespace-nowrap text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0 hover:bg-accent rounded text-gray-400 hover:text-gray-600 h-4 sm:h-5 px-1.5 sm:px-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy w-2.5 h-2.5 sm:w-3 sm:h-3" aria-hidden="true">
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                    </svg>
                    <span className="hidden sm:inline text-xs">Copy</span>
                  </button>
                  <button
                    onClick={() => retryMessage(index)}
                    className="inline-flex items-center justify-center gap-1 whitespace-nowrap text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0 hover:bg-accent rounded text-gray-400 hover:text-blue-600 h-4 sm:h-5 px-1.5 sm:px-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-refresh-cw w-2.5 h-2.5 sm:w-3 sm:h-3" aria-hidden="true">
                      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                      <path d="M21 3v5h-5"></path>
                      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                      <path d="M8 16H3v5"></path>
                    </svg>
                    <span className="hidden sm:inline text-xs">Retry</span>
                  </button>
                  <span className="text-xs text-gray-500 ml-auto">{formatTime(message.timestamp)}</span>
                  </div>
                )}
              
              {/* Timestamp for user messages */}
              {message.role === 'user' && (
                <p className="text-xs text-gray-500 mt-1 text-right">{formatTime(message.timestamp)}</p>
              )}
            </div>
          </div>
        </div>
      ))}
      
      {/* Streaming text display */}
      {isStreaming && streamingText && (
        <div className="flex gap-2">
          <span className="relative flex shrink-0 overflow-hidden rounded-full w-6 h-6 mt-1 flex-shrink-0">
            <span className="flex h-full w-full items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold text-xs">R</span>
          </span>
          <div className="flex-1 max-w-[280px] xs:max-w-sm sm:max-w-md md:max-w-3xl lg:max-w-3xl xl:max-w-4xl 2xl:max-w-7xl">
            <div className="bg-gray-50 rounded-lg rounded-tl-md px-3 py-2 border border-gray-200">
              <div className="text-sm leading-relaxed mb-0">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={components}
                >
                  {formatMessage(streamingText)}
                </ReactMarkdown>
                {/* Blinking typing cursor */}
                <span className="inline-block w-2 h-4 bg-blue-500 ml-1 animate-pulse"></span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Loading indicator */}
      {loadingMessage && !isStreaming && (
        <div className="flex gap-2">
          <span className="relative flex shrink-0 overflow-hidden rounded-full w-6 h-6 mt-1 flex-shrink-0">
            <span className="flex h-full w-full items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold text-xs">R</span>
          </span>
          <div className="flex-1 max-w-[280px] xs:max-w-sm sm:max-w-md md:max-w-3xl lg:max-w-3xl xl:max-w-4xl 2xl:max-w-7xl">
            <div className="bg-gray-50 rounded-lg rounded-tl-md px-3 py-2 border border-gray-200">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
                <span className="text-xs text-gray-500">RazorpayGPT is thinking...</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div ref={endOfMessagesRef} />
    </div>
  );
};

export default MessageList;